import { useLazyQuery } from "@apollo/client/react";
import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { downloadFile } from "utils/file";

import { GET_INTENT_PROJECT_HCL } from "./gql";

const DEFAULT_PAGE_SIZE = 50;

const useIntentProjectDownloadHcl = () => {
  const { onError, reportInfo, reportSuccess, reportError } = useTypedContext(FlashContext);

  const [fetchHcl, { loading }] = useLazyQuery(GET_INTENT_PROJECT_HCL, { errorPolicy: "all" });

  const downloadHcl = useCallback(
    async (id: string) => {
      try {
        let page = 1;
        let hasMore = true;
        const pages: Array<{ name: string; hcl: string; hasMore: boolean }> = [];

        // Fetch all pages first to determine if we have multiple files
        while (hasMore) {
          const { data, error } = await fetchHcl({
            variables: {
              id,
              page,
              pageSize: DEFAULT_PAGE_SIZE,
            },
          });

          if (error) {
            reportError({
              title: "Failed to fetch Intent Project HCL",
              message: error.message,
            });
            return;
          }

          if (!data?.intentProject) {
            reportError({
              message: "Failed to fetch Intent Project HCL",
            });
            return;
          }

          const { name, hcl: hclResult } = data.intentProject;

          if (!hclResult) {
            reportError({
              title: "Failed to fetch Intent Project HCL",
              message: "HCL data not available",
            });
            return;
          }

          pages.push({
            name,
            hcl: hclResult.hcl,
            hasMore: hclResult.hasMore,
          });

          hasMore = hclResult.hasMore;
          page++;
        }

        const totalFiles = pages.length;
        const isMultipleFiles = totalFiles > 1;

        // Download files
        pages.forEach((pageData, index) => {
          const pageNumber = index + 1;
          const fileName = isMultipleFiles
            ? `${pageData.name}-${pageNumber}.tf`
            : `${pageData.name}.tf`;

          if (isMultipleFiles) {
            reportInfo({ message: `Downloading ${fileName}...` });
          }

          downloadFile(fileName, pageData.hcl);
        });

        if (isMultipleFiles) {
          reportSuccess({
            message: `Successfully downloaded ${totalFiles} HCL files`,
          });
        } else {
          reportSuccess({
            message: `Successfully downloaded HCL file`,
          });
        }
      } catch (error) {
        onError(error);
      }
    },
    [fetchHcl, onError, reportInfo, reportSuccess, reportError]
  );

  return {
    downloadHcl,
    loading,
  };
};

export default useIntentProjectDownloadHcl;
